import {
  Align,
  Modal,
  Spacer,
  Split,
  SplitSection,
  Text,
  TextButton,
} from '@loomhq/lens';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { INFO } from 'constants/error-severities';
import {
  RECORDING_LIMIT_MODAL_CLICKED,
  RECORDING_LIMIT_MODAL_DISMISSED,
  RECORDING_LIMIT_MODAL_SHOWN,
  RECORDING_LIMIT_TOAST_CLICKED,
} from 'constants/events';
import { ORG_ROLE_ADMIN } from 'constants/organizationRoles';
import { track } from 'utilities/analytics';
import { getGraphQLClient } from 'utilities/graphql';
import * as logger from 'utilities/loggerx';

import OutageSrc from 'img/video-player/outage.png';
import { useGetUserRoleForSelectedWorkspace } from 'hooks/workspace';

import UpdateUserReachedRecordingLimitChecklistItem from './updateUserReachedRecordingLimitChecklistItem.gql';
import { showConfirmationModal } from '../../../actions/confirmationModal';

import UpgradeButton from 'components/upgrade-button';

const RecordingTimerPaywallModal = ({
  user,
  renderToast,
  videoDurationLimit,
}) => {
  const dispatch = useDispatch();
  const userRole = useGetUserRoleForSelectedWorkspace();
  const [showModal, setShowModal] = useState(!renderToast);

  const analyticsData = useMemo(() => {
    return {
      userId: window.loomSSRUser.id,
      userEmail: window.loomSSRUser.email,
    };
  }, []);

  const videoDurationLimitToString = videoDurationLimit
    ? videoDurationLimit < 60
      ? `${videoDurationLimit} second`
      : `${videoDurationLimit / 60} minute`
    : '';

  useEffect(() => {
    if (!showModal) {
      return;
    }

    const updateChecklistItem = async () => {
      const gqlClient = getGraphQLClient();

      try {
        await gqlClient.mutate({
          mutation: UpdateUserReachedRecordingLimitChecklistItem,
          variables: {
            userId: user.id,
          },
        });
      } catch (err) {
        logger.warning(err, { userId: user.id }, { team: logger.COMPANY_TEAM });
      }
    };

    track(RECORDING_LIMIT_MODAL_SHOWN, analyticsData);
    updateChecklistItem();
  }, [analyticsData, showModal, user.id]);

  useEffect(() => {
    if (!renderToast) {
      return;
    }

    const toastMessage = (
      <Split gap="medium" alignment="center">
        <SplitSection basis={35}>
          <Text size="small" fontWeight="bold">
            You&apos;ve hit the {videoDurationLimitToString} recording limit.
          </Text>
          {userRole === ORG_ROLE_ADMIN ? (
            <Text size="small">
              Create videos with unlimited recording time on the{' '}
              <strong>Business plan</strong> or above. Click to upgrade.
            </Text>
          ) : (
            <Text size="small">
              Create videos with unlimited recording time on the{' '}
              <strong>Business plan</strong> or above. Click to send a request
              to your admin to upgrade.
            </Text>
          )}
        </SplitSection>
        <SplitSection grow={1}>
          <UpgradeButton
            analyticsEvent={RECORDING_LIMIT_TOAST_CLICKED}
            hideTooltip
          />
        </SplitSection>
      </Split>
    );

    dispatch(showConfirmationModal(toastMessage, INFO, 6000));
  }, [
    analyticsData,
    dispatch,
    renderToast,
    userRole,
    videoDurationLimitToString,
  ]);

  const onClose = () => {
    track(RECORDING_LIMIT_MODAL_DISMISSED, analyticsData);
    setShowModal(false);
  };

  return (
    <Modal
      isOpen={showModal}
      onCloseClick={onClose}
      onBackgroundClick={onClose}
      maxWidth={55}
    >
      <Align alignment="center">
        <img src={OutageSrc} height="88" width="77"></img>
      </Align>
      <Spacer bottom={2} />
      <Text fontWeight="bold" alignment="center" size="large">
        You&apos;ve got a lot to say!
      </Text>
      <Spacer bottom={2} />
      <Text alignment="center" color="bodyDimmed">
        You&apos;ve hit the {videoDurationLimitToString} recording limit. If you
        have more to add, upgrade for unlimited recording time.
      </Text>
      <Spacer bottom={2} />
      <UpgradeButton
        analyticsEvent={RECORDING_LIMIT_MODAL_CLICKED}
        defaultButtonText="Upgrade for unlimited recordings"
        hasFullWidth
        size="large"
      />
      <Spacer bottom={2} />
      <Align alignment="center">
        <TextButton onClick={onClose}>Go to my video</TextButton>
      </Align>
    </Modal>
  );
};

export default RecordingTimerPaywallModal;
